// Extracted performance components for PerformancePage.

import type { JSX } from "react";

export interface AgencyPerformance {
  total_carers: number;
  active_carers_90d: number;
  avg_csat_90d: number | null;
  on_time_rate_90d: number | null;
  report_submission_rate_90d: number | null;
  no_show_count_90d: number;
  compliance_rate_90d: number | null;
  tier_counts: { tier1: number; tier2: number; tier3: number };
}

export interface CarerPerformance {
  carer_id: string;
  full_name: string;
  tier: 1 | 2 | 3;
  total_visits_90d: number;
  avg_csat_90d: number | null;
  on_time_rate_90d: number | null;
  report_submission_rate_90d: number | null;
  no_show_count_90d: number;
}

const CLS_RED = "text-red-600";
const CLS_GREEN = "text-green-600";

function pct(val: number | null): string {
  if (val === null) return "—";
  return `${(val * 100).toFixed(1)}%`;
}

function csatColor(val: number | null): string {
  if (val === null) return "text-slate-500";
  if (val >= 4.5) return CLS_GREEN;
  if (val >= 3.8) return "text-amber-600";
  return CLS_RED;
}

function TierBadge({ tier }: { tier: 1 | 2 | 3 }): JSX.Element {
  const styles = { 1: "bg-green-100 text-green-700", 2: "bg-blue-100 text-blue-700", 3: "bg-slate-100 text-slate-500" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[tier]}`}>
      Tier {tier}
    </span>
  );
}

interface StatTileProps { label: string; value: string | number; accent?: string; }
function StatTile({ label, value, accent }: StatTileProps): JSX.Element {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${accent ?? "text-slate-900"}`}>{value}</p>
    </div>
  );
}

export function AgencySummary({ d }: { d: AgencyPerformance }): JSX.Element {
  return (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-slate-700">Agency (90-day)</h2>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Active Carers" value={`${d.active_carers_90d} / ${d.total_carers}`} accent="text-navy-800" />
        <StatTile label="Avg CSAT" value={d.avg_csat_90d?.toFixed(2) ?? "—"} accent={csatColor(d.avg_csat_90d)} />
        <StatTile label="On-Time Rate" value={pct(d.on_time_rate_90d)} />
        <StatTile label="Report Rate" value={pct(d.report_submission_rate_90d)} />
        <StatTile label="No-Shows" value={d.no_show_count_90d} accent={d.no_show_count_90d > 0 ? CLS_RED : undefined} />
        <StatTile label="Compliance Rate" value={pct(d.compliance_rate_90d)} />
        <StatTile label="Tier 1 Carers" value={d.tier_counts.tier1} accent={CLS_GREEN} />
        <StatTile label="Tier 2 / Tier 3" value={`${d.tier_counts.tier2} / ${d.tier_counts.tier3}`} />
      </div>
    </div>
  );
}

export function CarerTable({ carers }: { carers: CarerPerformance[] }): JSX.Element {
  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold text-slate-700">Carer Breakdown</h2>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Carer</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 uppercase">Tier</th>
              <th className="py-2.5 px-4 text-right text-xs font-medium text-slate-500 uppercase">Visits</th>
              <th className="py-2.5 px-4 text-right text-xs font-medium text-slate-500 uppercase">Avg CSAT</th>
              <th className="py-2.5 px-4 text-right text-xs font-medium text-slate-500 uppercase">On-Time</th>
              <th className="py-2.5 px-4 text-right text-xs font-medium text-slate-500 uppercase">Report Rate</th>
              <th className="py-2.5 px-4 text-right text-xs font-medium text-slate-500 uppercase">No-Shows</th>
            </tr>
          </thead>
          <tbody>
            {carers.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-400">No carer data.</td></tr>
            )}
            {carers.map((c) => (
              <tr key={c.carer_id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4 text-sm font-medium text-slate-900">{c.full_name}</td>
                <td className="py-3 px-4"><TierBadge tier={c.tier} /></td>
                <td className="py-3 px-4 text-sm text-right text-slate-600">{c.total_visits_90d}</td>
                <td className={`py-3 px-4 text-sm text-right font-medium ${csatColor(c.avg_csat_90d)}`}>
                  {c.avg_csat_90d?.toFixed(2) ?? "—"}
                </td>
                <td className="py-3 px-4 text-sm text-right text-slate-600">{pct(c.on_time_rate_90d)}</td>
                <td className="py-3 px-4 text-sm text-right text-slate-600">{pct(c.report_submission_rate_90d)}</td>
                <td className={`py-3 px-4 text-sm text-right font-medium ${c.no_show_count_90d > 0 ? CLS_RED : "text-slate-600"}`}>
                  {c.no_show_count_90d}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
